import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { Circle, MapContainer, TileLayer, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlaceIcon from "@mui/icons-material/Place";

// Fix for Leaflet marker icons
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { useSelector } from "react-redux";
import { formatFinance } from "../../../../utils/formatters";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Animation variants
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

// Utility for getting color based on delivery count
const getHeatmapColor = (count, max) => {
  const ratio = Math.min(count / (max || 1), 1);

  if (ratio > 0.75) return "#d32f2f"; // High - red
  if (ratio > 0.5) return "#f57c00"; // Medium-high - orange
  if (ratio > 0.25) return "#fbc02d"; // Medium - yellow
  return "#4caf50"; // Low - green
};

const DeliveryHeatmapMap = ({ data, loading }) => {
  const theme = useTheme();
  const [mapZoom, setMapZoom] = useState(12);
  const [visualizationType, setVisualizationType] = useState("count");
  const [maxCount, setMaxCount] = useState(0);
  const [maxRevenue, setMaxRevenue] = useState(0);

  const deliveryCenterLat = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lat || 0
  );
  const deliveryCenterLng = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lng || 0
  );

  useEffect(() => {
    if (data?.heatmap_data && data.heatmap_data.length > 0) {
      // Calculate max values for color scaling
      const counts = data.heatmap_data.map((point) => point.delivery_count);
      setMaxCount(Math.max(...counts));

      const revenues = data.heatmap_data.map((point) => point.total_revenue);
      setMaxRevenue(Math.max(...revenues));

      // Try to center map on the most active point
      const mostActivePoint = data.heatmap_data.reduce(
        (prev, current) =>
          current.delivery_count > prev.delivery_count ? current : prev,
        data.heatmap_data[0]
      );
    }
  }, [data]);

  const handleVisualizationChange = (event) => {
    setVisualizationType(event.target.value);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 500,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data || !data.heatmap_data || data.heatmap_data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Sem dados disponíveis para o período selecionado
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} component={motion.div} variants={itemVariants}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LocationOnIcon sx={{ mr: 1, color: "primary.main" }} />
                Mapa de Calor de Entregas
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visualização geográfica da concentração de entregas por região
            </Typography>
          </Box>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="visualization-type-label">Visualização</InputLabel>
            <Select
              labelId="visualization-type-label"
              id="visualization-type"
              value={visualizationType}
              label="Visualização"
              onChange={handleVisualizationChange}
            >
              <MenuItem value="count">Quantidade de Entregas</MenuItem>
              <MenuItem value="revenue">Faturamento Total</MenuItem>
              <MenuItem value="average">Ticket Médio</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            height: 500,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <MapContainer
            center={[deliveryCenterLat, deliveryCenterLng]}
            zoom={mapZoom}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {data.heatmap_data.map((point, index) => {
              const lat = parseFloat(point.fk_address__lat);
              const lng = parseFloat(point.fk_address__long);

              if (isNaN(lat) || isNaN(lng)) return null;

              let radius, color, value;

              switch (visualizationType) {
                case "revenue":
                  value = point.total_revenue;
                  radius = Math.max(50, (value / maxRevenue) * 200);
                  color = getHeatmapColor(value, maxRevenue);
                  break;
                case "average":
                  value = point.avg_order_value;
                  radius = Math.max(50, (value / 150) * 200); // Assuming 150 is a high avg order value
                  color =
                    value > 100
                      ? "#9c27b0"
                      : value > 75
                      ? "#3f51b5"
                      : value > 50
                      ? "#2196f3"
                      : "#4caf50";
                  break;
                case "count":
                default:
                  value = point.delivery_count;
                  radius = Math.max(50, (value / maxCount) * 200);
                  color = getHeatmapColor(value, maxCount);
              }

              return (
                <Circle
                  key={`${lat}-${lng}-${index}`}
                  center={[lat, lng]}
                  radius={radius}
                  pathOptions={{
                    fillColor: color,
                    fillOpacity: 0.6,
                    weight: 1,
                    color: alpha(color, 0.8),
                  }}
                >
                  <Tooltip>
                    <div>
                      <strong>Bairro:</strong>{" "}
                      {point.fk_address__neighborhood || "Desconhecido"}
                      <br />
                      <strong>Entregas:</strong> {point.delivery_count}
                      <br />
                      <strong>Faturamento:</strong>{" "}
                      {formatFinance(point.total_revenue)}
                      <br />
                      <strong>Ticket Médio:</strong>{" "}
                      {formatFinance(point.avg_order_value)}
                    </div>
                  </Tooltip>
                </Circle>
              );
            })}
          </MapContainer>
        </Paper>
      </Grid>

      <Grid item xs={12} component={motion.div} variants={itemVariants}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Resumo de Entregas
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total de Entregas
              </Typography>
              <Typography variant="h5">
                {data.total_deliveries.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Período Analisado
              </Typography>
              <Typography variant="h5">
                {new Date(data.date_range.start).toLocaleDateString()} até{" "}
                {new Date(data.date_range.end).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Regiões com Entregas
              </Typography>
              <Typography variant="h5">
                {
                  new Set(
                    data.heatmap_data.map((p) => p.fk_address__neighborhood)
                  ).size
                }
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Concentração por Região:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {data.heatmap_data
                .sort((a, b) => b.delivery_count - a.delivery_count)
                .slice(0, 10)
                .map((point, index) => (
                  <Chip
                    key={index}
                    icon={<PlaceIcon />}
                    label={`${
                      point.fk_address__neighborhood || "Desconhecido"
                    }: ${point.delivery_count}`}
                    color={index < 3 ? "primary" : "default"}
                    size="small"
                    variant={index < 3 ? "filled" : "outlined"}
                  />
                ))}
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DeliveryHeatmapMap;
